import type { CartItem, CartItemOptionSelection } from "@/types/cart";
import type { PublicOrderItemPayload } from "@/types/order";
import type { CompositionSelection } from "@/types/food-configuration";
import type { PublicProductOptionGroup } from "@/types/store";
import { ApiError } from "@/lib/api";

export function configurationError(error: unknown): string {
  const messages = (value: unknown): string[] => {
    if (typeof value === "string") return [value];
    if (Array.isArray(value)) return value.flatMap(messages);
    if (value && typeof value === "object")
      return Object.values(value).flatMap(messages);
    return [];
  };
  if (error instanceof ApiError)
    return messages(error.data).join(" ") || error.message;
  return error instanceof Error
    ? error.message
    : "Não foi possível atualizar a configuração.";
}

export function fractionLabel(value?: string): string {
  if (!value || value === "1") return "";
  return (
    (
      {
        "1/2": "½",
        "0.5": "½",
        "1/3": "⅓",
        "2/3": "⅔",
        "1/4": "¼",
        "0.25": "¼",
        "3/4": "¾",
        "0.75": "¾",
      } as Record<string, string>
    )[value] ?? value
  );
}

function normalizedFraction(value: string) {
  // Identity only: price and fraction validation belong to the server.
  if (!/^(?:\d+(?:\.\d+)?|\.\d+|\d+\/\d+)$/.test(value) || value.length > 40)
    return value;
  const decimals = value.split(".")[1]?.length ?? 0;
  let numerator = BigInt(
    value.includes("/") ? value.split("/")[0] : value.replace(".", ""),
  );
  let denominator = value.includes("/")
    ? BigInt(value.split("/")[1])
    : BigInt(10) ** BigInt(decimals);
  if (denominator === BigInt(0)) return value;
  const gcd = (a: bigint, b: bigint): bigint =>
    b !== BigInt(0) ? gcd(b, a % b) : a;
  const divisor = gcd(numerator, denominator);
  numerator /= divisor;
  denominator /= divisor;
  return denominator === BigInt(1)
    ? String(numerator)
    : `${numerator}/${denominator}`;
}

export function foodFlavorCounts(group: PublicProductOptionGroup, variationId: number | null): number[] {
  const rule = group.variation_rules?.find(rule => rule.variation === variationId);
  const minimum = rule?.min_selections ?? group.min_selections;
  const maximum = rule?.max_selections ?? group.max_selections;
  const allowed = (rule?.allowed_fractions ?? []).map(normalizedFraction);
  return [1, 2].filter(count => count >= minimum && count <= maximum && (
    allowed.length === 0 || allowed.includes(count === 1 ? "1" : "1/2")
  ));
}

export function compositionKey(selections: CompositionSelection[] = []) {
  return [...selections]
    .sort((a, b) => a.option_id - b.option_id)
    .map((entry) => `${entry.option_id}@${normalizedFraction(entry.fraction)}`)
    .join(",");
}

export function cartItemPayload(item: CartItem): PublicOrderItemPayload {
  const composition = item.composition_selections ?? [];
  const compositionIds = new Set(composition.map((entry) => entry.option_id));
  return {
    product: item.product.id,
    variation: item.variation?.id ?? null,
    quantity: item.quantity,
    selected_option_ids: (item.selected_options ?? [])
      .filter((option) => !compositionIds.has(option.option_id))
      .map((option) => option.option_id),
    composition_selections: composition,
    observation: item.observation ?? "",
  };
}

export function normalizeSavedCart(value: unknown): CartItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter(
      (item) =>
        item &&
        item.product &&
        Number.isInteger(item.product.id) &&
        Number.isInteger(item.quantity) &&
        item.quantity > 0,
    )
    .map((item) => ({
      ...item,
      variation: item.variation ?? null,
      selected_options: Array.isArray(item.selected_options)
        ? item.selected_options.filter((entry: CartItemOptionSelection | null) => entry && Number.isInteger(entry.option_id))
        : [],
      composition_selections: Array.isArray(item.composition_selections)
        ? item.composition_selections.filter((entry: CompositionSelection | null) => entry && Number.isInteger(entry.option_id) && typeof entry.fraction === "string")
        : [],
      observation: typeof item.observation === "string" ? item.observation : "",
      quoted_unit_price: undefined,
    }));
}
